import { STANDARD_WORK_TRAINER_CERT_BY_CATEGORY } from '../constants/StandardWorkTrainerCertMap';

/** A single data row parsed from the Certified Standard Work Trainers CSV export. */
export interface ParsedBackfillRow {
    rowNumber: number;          // 1-based line number in the source CSV, for reporting
    empNo: string;              // Workday employee number == WorkdayEmployeeCertification.EmployeeId
    trainerName: string;        // Certified Trainer — an EMAIL in the raw export; verified against empNo
    categories: string[];       // one or more "Certified For" values
    jobTitle: string;
    issuedDate: string;         // YYYY-MM-DD (from Effective Date)
    expirationDate: string;     // YYYY-MM-DD
    /**
     * The Certified By cell verbatim — an EMAIL in the raw export, and possibly several of them.
     * Kept whole so resolution can see every certifier and prefer a General Superintendent; split with
     * splitCertifierEmails, which accepts both the ';' the current export uses and SharePoint's ';#'
     * multi-value form. '' when the cell is empty.
     */
    certifiedByName: string;
}

/** A prospective certification: one row expanded per category, its category mapped to a cert. */
export interface ExpandedBackfillRecord {
    rowNumber: number;
    empNo: string;
    trainerName: string;
    category: string;
    certName: string;           // matched WorkdayCertification name (resolved to an id at runtime)
    issuedDate: string;
    expirationDate: string;
    certifiedByName: string;
}

/** A row/category that will not be imported, paired with the reason. */
export interface SkippedBackfillRecord {
    rowNumber: number;
    empNo: string;
    category: string;
    reason: string;
}

/**
 * Splits one CSV line into fields, honoring double-quoted values (which may contain
 * commas) and the RFC-4180 "" escape for a literal quote.
 */
export const parseCsvLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"') {
                if (line[i + 1] === '"') { cur += '"'; i++; }
                else quoted = false;
            } else cur += ch;
        } else if (ch === '"') quoted = true;
        else if (ch === ',') { out.push(cur); cur = ''; }
        else cur += ch;
    }
    out.push(cur);
    return out;
};

/**
 * Splits a "Certified For" cell into categories. Handles the SharePoint multi-value
 * form ("A;#B;#C") and, defensively, a JSON-array form (["A","B"]) from older exports.
 */
export const splitCategories = (raw: string): string[] => {
    const value = (raw ?? '').trim();
    if (!value) return [];
    if (value.startsWith('[')) {
        try {
            const arr = JSON.parse(value);
            if (Array.isArray(arr)) return arr.map(v => String(v).trim()).filter(Boolean);
        } catch { /* not JSON — fall back to delimiter split */ }
    }
    return value.split(';#').map(v => v.trim()).filter(Boolean);
};

// The Certified By cell is carried through whole rather than reduced here: it can name more than one
// certifier, and resolution needs all of them so it can prefer a General Superintendent. Splitting is
// splitCertifierEmails' job — see the note on certifiedByName.

/** Normalizes a date cell to YYYY-MM-DD (accepts both date-only and ISO timestamp forms). */
const toDateOnly = (raw: string): string => (raw ?? '').trim().slice(0, 10);

/**
 * Parses the Certified Standard Work Trainers CSV export into structured rows.
 * Locates the header row by its known columns, so any leading metadata line
 * (e.g. a SharePoint ListSchema row) is ignored. Returns [] if no header is found.
 */
export const parseBackfillCsv = (text: string): ParsedBackfillRow[] => {
    const lines = text.replace(/^﻿/, '').split(/\r?\n/);

    let headerIndex = -1;
    let columns: string[] = [];
    for (let i = 0; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const cells = parseCsvLine(lines[i]).map(c => c.trim().toLowerCase());
        if (cells.includes('emp no.') && cells.includes('certified for')) {
            headerIndex = i;
            columns = cells;
            break;
        }
    }
    if (headerIndex === -1) return [];

    const col = (name: string) => columns.indexOf(name);
    const iEmp = col('emp no.');
    const iTrainer = col('certified trainer');
    const iFor = col('certified for');
    const iTitle = col('job title');
    const iEffective = col('effective date');
    const iExpiration = col('expiration date');
    const iBy = col('certified by');

    const rows: ParsedBackfillRow[] = [];
    for (let i = headerIndex + 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const cells = parseCsvLine(lines[i]);
        const get = (idx: number) => (idx >= 0 ? (cells[idx] ?? '').trim() : '');
        rows.push({
            // The file's own 1-based line number, so a reported row can be opened straight from the CSV.
            // Derived from the line index rather than counted, so it stays right whatever precedes the
            // header — a ListSchema line, none, or blank lines anywhere in the file.
            rowNumber: i + 1,
            empNo: get(iEmp),
            trainerName: get(iTrainer),
            categories: splitCategories(get(iFor)),
            jobTitle: get(iTitle),
            issuedDate: toDateOnly(get(iEffective)),
            expirationDate: toDateOnly(get(iExpiration)),
            certifiedByName: get(iBy),
        });
    }
    return rows;
};

/**
 * True when the row's certification has not expired as of todayIso (inclusive).
 * YYYY-MM-DD strings compare chronologically, so a lexical comparison is safe.
 */
export const isUnexpired = (row: ParsedBackfillRow, todayIso: string): boolean =>
    !!row.expirationDate && row.expirationDate >= todayIso;

/**
 * Expands each row into one record per category and maps the category to a cert name.
 * Categories with no confident mapping (and rows with no category) are reported as skipped.
 */
export const expandAndMap = (
    rows: ParsedBackfillRow[],
): { records: ExpandedBackfillRecord[]; skipped: SkippedBackfillRecord[] } => {
    const records: ExpandedBackfillRecord[] = [];
    const skipped: SkippedBackfillRecord[] = [];

    for (const row of rows) {
        if (row.categories.length === 0) {
            skipped.push({ rowNumber: row.rowNumber, empNo: row.empNo, category: '(none)', reason: 'No "Certified For" value' });
            continue;
        }
        for (const category of row.categories) {
            const certName = STANDARD_WORK_TRAINER_CERT_BY_CATEGORY[category];
            if (certName) {
                records.push({
                    rowNumber: row.rowNumber,
                    empNo: row.empNo,
                    trainerName: row.trainerName,
                    category,
                    certName,
                    issuedDate: row.issuedDate,
                    expirationDate: row.expirationDate,
                    certifiedByName: row.certifiedByName,
                });
            } else {
                skipped.push({ rowNumber: row.rowNumber, empNo: row.empNo, category, reason: 'No matching certification' });
            }
        }
    }
    return { records, skipped };
};

/**
 * Collapses duplicate (employee, cert) records to a single record, keeping the one
 * with the latest expiration date. Returns the kept records and the dropped duplicates.
 */
export const dedupeKeepLatestExpiration = (
    records: ExpandedBackfillRecord[],
): { kept: ExpandedBackfillRecord[]; dropped: ExpandedBackfillRecord[] } => {
    const best = new Map<string, ExpandedBackfillRecord>();
    const dropped: ExpandedBackfillRecord[] = [];

    for (const rec of records) {
        const key = `${rec.empNo}|${rec.certName}`;
        const existing = best.get(key);
        if (!existing) {
            best.set(key, rec);
        } else if (rec.expirationDate > existing.expirationDate) {
            dropped.push(existing);
            best.set(key, rec);
        } else {
            dropped.push(rec);
        }
    }
    return { kept: Array.from(best.values()), dropped };
};
